/**
 * FWB EDI (Cargo-IMP) Consolidated Parser, Hydration, and Submission Utilities
 */

export function extractAwbNumber(rawFwb) {
  if (!rawFwb || typeof rawFwb !== 'string') return '';
  const lines = rawFwb.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  for (let idx = 0; idx < Math.min(6, lines.length); idx++) {
    const match = lines[idx].match(/^(\d{3}-\d{7,8})/);
    if (match) return match[1];
  }
  return '';
}

function parseParty(pLines, partyCode) {
  if (!pLines || pLines.length === 0) return {};
  const party = {};
  const sublines = [];
  for (const line of pLines) {
    if (line.startsWith(`${partyCode}/`)) {
      const val = line.substring(4).trim();
      if (val) sublines.push(val);
    } else if (line.startsWith('/')) {
      const val = line.substring(1).trim();
      sublines.push(val);
    } else if (line !== partyCode) {
      sublines.push(line.trim());
    }
  }
  if (sublines.length === 0) return party;

  let nameIdx = 0;
  if (sublines.length >= 4 && sublines[0].length <= 14 && !sublines[0].includes(' ') && sublines[1].includes(' ')) {
    party.account = sublines[0];
    nameIdx = 1;
  }
  party.name = sublines[nameIdx] || '';

  const remaining = sublines.slice(nameIdx + 1);
  const streetParts = [];
  for (let rIdx = 0; rIdx < remaining.length; rIdx++) {
    const rLine = remaining[rIdx];
    const czMatch = rLine.match(/^([A-Z]{2})(?:\/([A-Z0-9\s-]+))?(?:\/([A-Z]{2})\/([0-9A-Z\s-+.]+))?$/);
    if (czMatch && (rIdx === remaining.length - 1 || rIdx === remaining.length - 2)) {
      party.country = czMatch[1];
      party.postalZipCode = czMatch[2] || '';
      if (czMatch[3]) party.contactType = czMatch[3];
      if (czMatch[4]) party.contactNumber = czMatch[4];
      continue;
    }
    if (rIdx === remaining.length - 2 || (rIdx === remaining.length - 1 && !party.cityPlace)) {
      if (rLine.includes('/')) {
        const [city, st] = rLine.split('/');
        party.cityPlace = city.trim();
        party.stateProvince = st ? st.trim() : '';
        continue;
      } else if (/^[A-Z\s]+$/i.test(rLine) && rLine.length <= 25) {
        party.cityPlace = rLine.trim();
        continue;
      }
    }
    streetParts.push(rLine);
  }
  if (streetParts.length > 0) party.address = streetParts.join(', ');
  return party;
}

export function parseFwbMessage(rawFwb) {
  if (!rawFwb || typeof rawFwb !== 'string') return {};
  const lines = rawFwb.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const result = {
    shipmentDetails: {},
    routingAndFlights: [],
    shipper: {},
    consignee: {},
    issuingCarriersAgent: {},
    accountingInformation: [],
    chargeDeclaration: {},
    ratingDetails: {},
    chargeSummary: {},
    shippersCertification: {},
    specialHandlingCodes: [],
    otherCustomsSecurityInformation: [],
  };

  for (let idx = 0; idx < Math.min(6, lines.length); idx++) {
    const m = lines[idx].match(/^(\d{3}-\d{7,8})([A-Z]{3})([A-Z]{3})(?:\/T?(\d+))?(?:([KL])([\d.]+))?(?:MC([\d.]+))?/);
    if (m) {
      result.awbNumber = m[1];
      result.shipmentDetails.origin = m[2];
      result.shipmentDetails.destination = m[3];
      if (m[4]) result.shipmentDetails.pieces = Number(m[4]);
      if (m[6]) result.shipmentDetails.weight = parseFloat(m[6]);
      if (m[5]) result.shipmentDetails.unit = m[5] === 'K' ? 'KG' : 'LB';
      break;
    }
  }

  let curSec = '';
  const sec = {};
  for (const l of lines) {
    if (l === 'FWB' || l.match(/^\/\d+$/) || l.match(/^\d{3}-\d{7,8}/)) continue;
    const p = l.startsWith('/') ? '' : l.split('/')[0].toUpperCase();
    if (['FLT', 'RTG', 'SHP', 'CNE', 'AGT', 'ACC', 'CVD', 'RTD', 'OTH', 'PPD', 'COL', 'CER', 'ISU', 'REF', 'SPH', 'SSR', 'OSI', 'OCI'].includes(p)) {
      curSec = p;
      if (!sec[curSec]) sec[curSec] = [];
      sec[curSec].push(l);
    } else if (curSec) {
      sec[curSec].push(l);
    }
  }

  if (sec['FLT']) {
    const fToks = sec['FLT'][0].replace(/^FLT\//, '').split('/');
    const rToks = sec['RTG'] ? sec['RTG'][0].replace(/^RTG\//, '').split('/') : [];
    const flts = [];
    for (let i = 0; i < fToks.length; i += 2) {
      const f = fToks[i] || '', d = fToks[i + 1] || '', r = rToks[Math.floor(i / 2)] || '';
      const c = f.length >= 2 && isNaN(f.substring(0, 2)) ? f.substring(0, 2).toUpperCase() : '';
      flts.push({
        to: r ? r.substring(0, 3).toUpperCase() : (i === 0 ? result.shipmentDetails.destination || '' : ''),
        byCarrier: c,
        flightNumber: f.substring(c.length),
        date: d 
      });
    }
    if (flts.length > 0) result.routingAndFlights = flts;
  }

  if (sec['SHP']) {
    const s = parseParty(sec['SHP'], 'SHP');
    if (s.account) { s.shippersAccount = s.account; delete s.account; }
    result.shipper = s;
  }
  if (sec['CNE']) {
    const c = parseParty(sec['CNE'], 'CNE');
    if (c.account) { c.consigneesAccount = c.account; delete c.account; }
    result.consignee = c;
  }

  if (sec['AGT']) {
    const agt = {};
    const toks = sec['AGT'][0].replace(/^AGT\/?/, '').split('/');
    if (toks.length >= 1 && toks[0]) {
      agt.accountNumber = toks[0];
    }
    if (toks.length >= 2 && toks[1]) {
      agt.iataCode = toks[1];
    }
    if (toks.length >= 3 && toks[2]) {
      agt.cassAddress = toks[2];
    }
    const subs = sec['AGT'].slice(1).map((l) => l.replace(/^\//, '').trim()).filter(Boolean);
    if (subs[0]) agt.name = subs[0];
    if (subs[1]) agt.city = subs[1];
    result.issuingCarriersAgent = agt;
  }

  if (sec['ACC']) {
    const acc = [];
    for (const l of sec['ACC']) {
      const [id, ...rest] = l.replace(/^ACC\/?/, '').replace(/^\//, '').split('/');
      if (id && rest.length > 0) acc.push({
        identifier: id.trim(),
        information: rest.join('/').trim() 
      });
    }
    if (acc.length > 0) result.accountingInformation = acc;
  }

  if (sec['CVD']) {
    const toks = sec['CVD'][0].replace(/^CVD\//, '').split('/');
    let curr = toks[0] || 'USD', chg = toks[1] || 'PP', pW = 'P', pO = 'P', nvd = 'NVD', ncv = 'NCV', ins = 'NIL';
    if (toks.length >= 6) {
      if (toks[2] && toks[2].length === 2 && !['NVD', 'NCV', 'XXX', 'NIL'].includes(toks[2])) {
        pW = toks[2][0]; pO = toks[2][1]; nvd = toks[3] || 'NVD'; ncv = toks[4] || 'NCV'; ins = toks[5] || 'NIL';
      } else if (toks.length >= 7) {
        pW = toks[2] ? toks[2][0] : 'P'; pO = toks[3] ? toks[3][0] : 'P'; nvd = toks[4] || 'NVD'; ncv = toks[5] || 'NCV'; ins = toks[6] || 'NIL';
      }
    }
    result.chargeDeclaration = {
      currency: curr,
      chgsCode: chg,
      pcWeightVal: pW,
      pcOther: pO,
      declaredValueForCarriage: nvd,
      declaredValueForCustoms: ncv,
      amountOfInsurance: ins 
    };
  }

  if (sec['RTD']) {
    const rtd = {};
    for (const l of sec['RTD']) {
      if (l.startsWith('RTD/')) {
        for (const tok of l.replace(/^RTD\//, '').split('/')) {
          if (tok.startsWith('P')) rtd.piecesRcp = Number(tok.substring(1));
          else if (tok.startsWith('K')) { rtd.grossWeight = parseFloat(tok.substring(1)); rtd.weightUnit = 'KG'; }
          else if (tok.startsWith('L')) { rtd.grossWeight = parseFloat(tok.substring(1)); rtd.weightUnit = 'LB'; }
          else if (tok.startsWith('C') && tok.length === 2) rtd.rateClass = tok.substring(1);
          else if (tok.startsWith('W')) rtd.chargeableWeight = parseFloat(tok.substring(1));
          else if (tok.startsWith('R')) rtd.rateCharge = tok.substring(1);
          else if (tok.startsWith('T')) rtd.totalCharge = tok.substring(1);
        }
      } else if (l.includes('/NG/') || l.includes('/NC/')) {
        const ng = l.split(/\/N[GC]\//)[1]; if (ng) rtd.natureOfGoods = [ng.trim()];
      } else if (l.includes('/NH/')) {
        const nh = l.split(/\/NH\//)[1]; if (nh) rtd.harmonisedCommodityCode = nh.trim();
      } else if (l.includes('/ND/')) {
        const ndMatch = l.match(/\/ND\/(?:[A-Z0-9.]+)?\/([A-Z]{3})?(\d+)-(\d+)-(\d+)\/(\d+)/);
        if (ndMatch) {
          const unit = ndMatch[1] === 'INH' ? 'IN' : 'CM';
          if (!rtd.dimensions) {
            rtd.dimensions = {
              unit,
              measurements: [] 
            };
          }
          rtd.dimensions.unit = unit;
          rtd.dimensions.measurements.push({
            length: ndMatch[2],
            width: ndMatch[3],
            height: ndMatch[4],
            pieces: ndMatch[5],
          });
        }
      } else if (l.includes('/NV/MC') || l.includes('/NV/')) {
        const volMatch = l.match(/\/NV\/(?:MC)?([0-9.]+)/);
        if (volMatch) {
          rtd.volume = volMatch[1];
        }
      }
    }
    if (!rtd.dimensions) {
      rtd.dimensions = {
        unit: '',
        measurements: [] 
      };
    }
    result.ratingDetails = rtd;
  }

  if (sec['PPD']) {
    const s = {
      weightCharge: {},
      total: {} 
    };
    for (const l of sec['PPD']) {
      const wt = l.match(/WT([\d.]+)/); if (wt) s.weightCharge.prepaid = wt[1];
      const ct = l.match(/CT([\d.]+)/); if (ct) s.total.prepaid = ct[1];
    }
    result.chargeSummary = s;
  } else if (sec['COL']) {
    const s = {
      weightCharge: {},
      total: {} 
    };
    for (const l of sec['COL']) {
      const wt = l.match(/WT([\d.]+)/); if (wt) s.weightCharge.collect = wt[1];
      const ct = l.match(/CT([\d.]+)/); if (ct) s.total.collect = ct[1];
    }
    result.chargeSummary = s;
  }

  if (sec['CER']) {
    result.shippersCertification = { shipperAgentsName: sec['CER'][0].replace(/^CER\//, '').trim() };
  }
  if (sec['SPH']) {
    const sph = [];
    for (const l of sec['SPH']) {
      for (const tok of l.replace(/^SPH\/?/, '').split('/')) if (tok.trim()) sph.push(tok.trim());
    }
    if (sph.length > 0) result.specialHandlingCodes = sph;
  }
  if (sec['OCI']) {
    const oci = [];
    for (const l of sec['OCI']) {
      const [c = '', t = '', code = '', ...rest] = l.replace(/^OCI\/?/, '').replace(/^\//, '').split('/');
      if (c && t && code) oci.push({
        countryCode: c.trim(),
        informationType: t.trim(),
        informationCode: code.trim(),
        information: rest.join('/').trim() 
      });
    }
    if (oci.length > 0) result.otherCustomsSecurityInformation = oci;
  }

  return result;
}

const pick = (a, b, def = '') => (a !== undefined && a !== null && a !== '' ? a : (b !== undefined && b !== null && b !== '' ? b : def));

export function hydrateFormDataFromFwb(existingData, rawFwb) {
  if (!rawFwb || typeof rawFwb !== 'string') return existingData || {};
  const p = parseFwbMessage(rawFwb);
  if (!existingData) return p;

  const m = { ...existingData };
  m.awbNumber = pick(m.awbNumber, p.awbNumber);

  const eSd = m.shipmentDetails || {}, pSd = p.shipmentDetails || {};
  m.shipmentDetails = {
    origin: pick(pSd.origin, eSd.origin),
    destination: pick(pSd.destination, eSd.destination),
    pieces: pick(pSd.pieces, eSd.pieces),
    weight: pick(pSd.weight, eSd.weight),
    unit: pick(pSd.unit, eSd.unit, 'KG'),
  };

  if (Array.isArray(p.routingAndFlights) && p.routingAndFlights.length > 0) {
    m.routingAndFlights = p.routingAndFlights;
  }

  const eS = m.shipper || {}, pS = p.shipper || {};
  m.shipper = {
    name: pick(eS.name, pS.name),
    address: pick(pS.address, eS.address),
    cityPlace: pick(pS.cityPlace, eS.cityPlace),
    stateProvince: pick(pS.stateProvince, eS.stateProvince),
    country: pick(pS.country, eS.country),
    postalZipCode: pick(pS.postalZipCode, eS.postalZipCode),
    contactType: pick(pS.contactType, eS.contactType, 'TE'),
    contactNumber: pick(pS.contactNumber, eS.contactNumber),
    shippersAccount: pick(pS.shippersAccount, eS.shippersAccount),
  };

  const eC = m.consignee || {}, pC = p.consignee || {};
  m.consignee = {
    name: pick(eC.name, pC.name),
    address: pick(pC.address, eC.address),
    cityPlace: pick(pC.cityPlace, eC.cityPlace),
    stateProvince: pick(pC.stateProvince, eC.stateProvince),
    country: pick(pC.country, eC.country),
    postalZipCode: pick(pC.postalZipCode, eC.postalZipCode),
    contactType: pick(pC.contactType, eC.contactType, 'TE'),
    contactNumber: pick(pC.contactNumber, eC.contactNumber),
    consigneesAccount: pick(pC.consigneesAccount, eC.consigneesAccount),
  };

  const eA = m.issuingCarriersAgent || {}, pA = p.issuingCarriersAgent || {};
  m.issuingCarriersAgent = {
    name: pick(pA.name, eA.name),
    city: pick(pA.city, eA.city),
    iataCode: pick(pA.iataCode, eA.iataCode),
    accountNumber: pick(pA.accountNumber, eA.accountNumber),
  };

  if (Array.isArray(p.accountingInformation) && p.accountingInformation.length > 0) {
    m.accountingInformation = p.accountingInformation;
  }

  const eCd = m.chargeDeclaration || {}, pCd = p.chargeDeclaration || {};
  m.chargeDeclaration = {
    currency: pick(pCd.currency, eCd.currency, 'USD'),
    chgsCode: pick(pCd.chgsCode, eCd.chgsCode, 'PP'),
    pcWeightVal: pick(pCd.pcWeightVal, eCd.pcWeightVal, 'P'),
    pcOther: pick(pCd.pcOther, eCd.pcOther, 'P'),
    declaredValueForCarriage: pick(pCd.declaredValueForCarriage, eCd.declaredValueForCarriage, 'NVD'),
    declaredValueForCustoms: pick(pCd.declaredValueForCustoms, eCd.declaredValueForCustoms, 'NCV'),
    amountOfInsurance: pick(pCd.amountOfInsurance, eCd.amountOfInsurance, 'NIL'),
  };

  const eR = m.ratingDetails || {}, pR = p.ratingDetails || {};
  m.ratingDetails = {
    piecesRcp: pick(pR.piecesRcp, eR.piecesRcp),
    grossWeight: pick(pR.grossWeight, eR.grossWeight),
    weightUnit: pick(pR.weightUnit, eR.weightUnit, 'KG'),
    rateClass: pick(pR.rateClass, eR.rateClass, 'N'),
    commodityItemNo: pick(pR.commodityItemNo, eR.commodityItemNo),
    chargeableWeight: pick(pR.chargeableWeight, eR.chargeableWeight),
    rateCharge: pick(pR.rateCharge, eR.rateCharge),
    totalCharge: pick(pR.totalCharge, eR.totalCharge),
    harmonisedCommodityCode: pick(pR.harmonisedCommodityCode, eR.harmonisedCommodityCode),
    countryOfOrigin: pick(pR.countryOfOrigin, eR.countryOfOrigin),
    natureOfGoods: Array.isArray(pR.natureOfGoods) && pR.natureOfGoods.length > 0 ? pR.natureOfGoods : (Array.isArray(eR.natureOfGoods) ? eR.natureOfGoods : []),
    dimensions: pR.dimensions && (pR.dimensions.unit || (pR.dimensions.measurements && pR.dimensions.measurements.length > 0)) ? pR.dimensions : (eR.dimensions || {
      unit: '',
      measurements: [] 
    }),
    uldInformation: pR.uldInformation && pR.uldInformation.length > 0 ? pR.uldInformation : (eR.uldInformation || []),
    slac: pick(pR.slac, eR.slac),
  };

  if (p.chargeSummary && (p.chargeSummary.weightCharge?.prepaid || p.chargeSummary.total?.prepaid)) {
    m.chargeSummary = p.chargeSummary;
  }

  m.shippersCertification = { shipperAgentsName: pick(p.shippersCertification?.shipperAgentsName, m.shippersCertification?.shipperAgentsName) };

  if (Array.isArray(p.specialHandlingCodes) && p.specialHandlingCodes.length > 0) {
    m.specialHandlingCodes = p.specialHandlingCodes;
  }

  if (Array.isArray(p.otherCustomsSecurityInformation) && p.otherCustomsSecurityInformation.length > 0) {
    m.otherCustomsSecurityInformation = p.otherCustomsSecurityInformation;
  }

  return m;
}

export function prepareFormDataForSubmission(currentFormData, modifiedPaths = {}, fwbMessage = '', initialFormData = null) {
  const base = currentFormData ? JSON.parse(JSON.stringify(currentFormData)) : {};
  const baseFwb = parseFwbMessage(fwbMessage);

  const awbNum = base.awbNumber || initialFormData?.awbNumber || baseFwb.awbNumber || '';

  const isTouched = (path) => Boolean(modifiedPaths && modifiedPaths[path]);
  const hasTouchedPrefix = (prefix) => modifiedPaths && Object.keys(modifiedPaths).some((k) => k.startsWith(prefix));

  const pickField = (curVal, baseVal, path) => {
    if (isTouched(path)) return curVal;
    if (curVal !== undefined && curVal !== null && curVal !== '') return curVal;
    if (baseVal !== undefined && baseVal !== null && baseVal !== '') return baseVal;
    return undefined;
  };

  const payloadData = {
    docId: base.docId,
    awbNumber: awbNum,
    awbSrc: base.awbSrc || '',
  };

  // 1. Shipment Details
  const bSd = baseFwb.shipmentDetails || {}, cSd = base.shipmentDetails || {};
  payloadData.shipmentDetails = {
    origin: pickField(cSd.origin, bSd.origin, 'shipmentDetails.origin') || '',
    destination: pickField(cSd.destination, bSd.destination, 'shipmentDetails.destination') || '',
    pieces: pickField(cSd.pieces, bSd.pieces, 'shipmentDetails.pieces'),
    weight: pickField(cSd.weight, bSd.weight, 'shipmentDetails.weight'),
    unit: pickField(cSd.unit, bSd.unit, 'shipmentDetails.unit') || 'KG',
  };

  // 2. Routing & Flights
  if (hasTouchedPrefix('routingAndFlights')) {
    payloadData.routingAndFlights = (base.routingAndFlights || []).filter((f) => f && (f.byCarrier || f.flightNumber || f.to));
  } else if (Array.isArray(baseFwb.routingAndFlights) && baseFwb.routingAndFlights.length > 0) {
    payloadData.routingAndFlights = baseFwb.routingAndFlights;
  } else if (Array.isArray(base.routingAndFlights)) {
    payloadData.routingAndFlights = base.routingAndFlights.filter((f) => f && (f.byCarrier || f.flightNumber || f.to));
  }

  // 3. Shipper
  const bShp = baseFwb.shipper || {}, cShp = base.shipper || {};
  const shp = {};
  for (const k of ['name', 'address', 'cityPlace', 'stateProvince', 'country', 'postalZipCode', 'contactType', 'contactNumber', 'shippersAccount']) {
    const val = pickField(cShp[k], bShp[k], `shipper.${k}`);
    if (val !== undefined) shp[k] = val;
  }
  if (Object.keys(shp).length > 0 || hasTouchedPrefix('shipper')) payloadData.shipper = shp;

  // 4. Consignee
  const bCne = baseFwb.consignee || {}, cCne = base.consignee || {};
  const cne = {};
  for (const k of ['name', 'address', 'cityPlace', 'stateProvince', 'country', 'postalZipCode', 'contactType', 'contactNumber', 'consigneesAccount']) {
    const val = pickField(cCne[k], bCne[k], `consignee.${k}`);
    if (val !== undefined) cne[k] = val;
  }
  if (Object.keys(cne).length > 0 || hasTouchedPrefix('consignee')) payloadData.consignee = cne;

  // 5. Agent
  const bAgt = baseFwb.issuingCarriersAgent || {}, cAgt = base.issuingCarriersAgent || {};
  const agt = {};
  for (const k of ['name', 'city', 'iataCode', 'accountNumber']) {
    const val = pickField(cAgt[k], bAgt[k], `issuingCarriersAgent.${k}`);
    if (val !== undefined) agt[k] = val;
  }
  if (Object.keys(agt).length > 0 || hasTouchedPrefix('issuingCarriersAgent')) payloadData.issuingCarriersAgent = agt;

  // 6. Accounting
  if (hasTouchedPrefix('accountingInformation')) {
    payloadData.accountingInformation = (base.accountingInformation || []).filter((i) => i && (i.identifier || i.information));
  } else if (Array.isArray(baseFwb.accountingInformation) && baseFwb.accountingInformation.length > 0) {
    payloadData.accountingInformation = baseFwb.accountingInformation;
  } else if (Array.isArray(base.accountingInformation)) {
    payloadData.accountingInformation = base.accountingInformation.filter((i) => i && (i.identifier || i.information));
  }

  // 7. Charge Declaration
  const bCd = baseFwb.chargeDeclaration || {}, cCd = base.chargeDeclaration || {};
  const cd = {};
  for (const k of ['currency', 'chgsCode', 'pcWeightVal', 'pcOther', 'declaredValueForCarriage', 'declaredValueForCustoms', 'amountOfInsurance']) {
    const val = pickField(cCd[k], bCd[k], `chargeDeclaration.${k}`);
    if (val !== undefined) cd[k] = val;
  }
  if (Object.keys(cd).length > 0 || hasTouchedPrefix('chargeDeclaration')) payloadData.chargeDeclaration = cd;

  // 8. Rating Details
  const bRtd = baseFwb.ratingDetails || {}, cRtd = base.ratingDetails || {};
  const rtd = {};
  for (const k of ['piecesRcp', 'grossWeight', 'weightUnit', 'rateClass', 'commodityItemNo', 'chargeableWeight', 'rateCharge', 'totalCharge', 'harmonisedCommodityCode', 'countryOfOrigin', 'slac']) {
    const val = pickField(cRtd[k], bRtd[k], `ratingDetails.${k}`);
    if (val !== undefined) rtd[k] = val;
  }
  if (hasTouchedPrefix('ratingDetails.natureOfGoods')) {
    rtd.natureOfGoods = cRtd.natureOfGoods || [];
  } else if (Array.isArray(bRtd.natureOfGoods) && bRtd.natureOfGoods.length > 0) {
    rtd.natureOfGoods = bRtd.natureOfGoods;
  } else if (Array.isArray(cRtd.natureOfGoods)) {
    rtd.natureOfGoods = cRtd.natureOfGoods;
  }
  if (hasTouchedPrefix('ratingDetails.dimensions')) {
    rtd.dimensions = cRtd.dimensions || {
      unit: '',
      measurements: [] 
    };
  } else if (bRtd.dimensions && Array.isArray(bRtd.dimensions.measurements) && bRtd.dimensions.measurements.length > 0) {
    rtd.dimensions = bRtd.dimensions;
  } else if (cRtd.dimensions && Array.isArray(cRtd.dimensions.measurements) && cRtd.dimensions.measurements.length > 0) {
    rtd.dimensions = cRtd.dimensions;
  }
  if (hasTouchedPrefix('ratingDetails.uldInformation')) {
    rtd.uldInformation = cRtd.uldInformation || [];
  } else if (Array.isArray(bRtd.uldInformation)) {
    rtd.uldInformation = bRtd.uldInformation;
  }
  if (Object.keys(rtd).length > 0 || hasTouchedPrefix('ratingDetails')) payloadData.ratingDetails = rtd;

  // 9. Charge Summary
  if (hasTouchedPrefix('chargeSummary')) {
    payloadData.chargeSummary = base.chargeSummary || {};
  } else if (baseFwb.chargeSummary && (baseFwb.chargeSummary.weightCharge?.prepaid || baseFwb.chargeSummary.total?.prepaid)) {
    payloadData.chargeSummary = baseFwb.chargeSummary;
  } else if (base.chargeSummary) {
    payloadData.chargeSummary = base.chargeSummary;
  }

  // 10. Shippers Certification
  const bCert = baseFwb.shippersCertification || {}, cCert = base.shippersCertification || {};
  const certVal = pickField(cCert.shipperAgentsName, bCert.shipperAgentsName, 'shippersCertification.shipperAgentsName');
  if (certVal !== undefined) payloadData.shippersCertification = { shipperAgentsName: certVal };

  // 11. Special Handling Codes
  if (hasTouchedPrefix('specialHandlingCodes')) {
    payloadData.specialHandlingCodes = (base.specialHandlingCodes || []).filter(Boolean);
  } else if (Array.isArray(baseFwb.specialHandlingCodes) && baseFwb.specialHandlingCodes.length > 0) {
    payloadData.specialHandlingCodes = baseFwb.specialHandlingCodes;
  } else if (Array.isArray(base.specialHandlingCodes)) {
    payloadData.specialHandlingCodes = base.specialHandlingCodes.filter(Boolean);
  }

  // 12. Customs Security Information
  if (hasTouchedPrefix('otherCustomsSecurityInformation')) {
    payloadData.otherCustomsSecurityInformation = (base.otherCustomsSecurityInformation || []).filter((i) => i && (i.countryCode || i.informationType || i.information));
  } else if (Array.isArray(baseFwb.otherCustomsSecurityInformation) && baseFwb.otherCustomsSecurityInformation.length > 0) {
    payloadData.otherCustomsSecurityInformation = baseFwb.otherCustomsSecurityInformation;
  } else if (Array.isArray(base.otherCustomsSecurityInformation)) {
    payloadData.otherCustomsSecurityInformation = base.otherCustomsSecurityInformation.filter((i) => i && (i.countryCode || i.informationType || i.information));
  }

  // 13. Other Charges
  if (hasTouchedPrefix('otherCharges')) {
    payloadData.otherCharges = (base.otherCharges || []).filter((c) => c && (c.code || c.amount));
  } else if (Array.isArray(baseFwb.otherCharges) && baseFwb.otherCharges.length > 0) {
    payloadData.otherCharges = baseFwb.otherCharges;
  } else if (Array.isArray(base.otherCharges)) {
    payloadData.otherCharges = base.otherCharges.filter((c) => c && (c.code || c.amount));
  }

  return payloadData;
}
